import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../CSS/JobUpload.css';

const EditMonthlyNews: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    monthlyNewsTitle: '',
    monthlyNewsDescription: '',
    newsArticleFile: null as File | null
  });

  const [descCharCount, setDescCharCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [monthlyNewsId, setMonthlyNewsId] = useState<number | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string>('');

  const MAX_DESC = 5000;

  // Helper function to convert database file path to API endpoint URL
  const getFileUrl = (filePath: string | null | undefined): string => {
    if (!filePath || !filePath.trim()) {
      return '';
    }
    // If path already starts with http, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    // Convert database path to API endpoint
    // Database path: Files/Monthly News/filename.pdf
    // API endpoint: /api/admin/monthly-news/file/Files/Monthly News/filename.pdf
    const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
    // Production API endpoint
    return `/api/admin/monthly-news/file/${cleanPath}`;
    // return `http://localhost:3060/api/admin/monthly-news/file/${cleanPath}`;
  };

  // Load data from query parameter on mount
  useEffect(() => {
    const idParam = searchParams.get('monthlyNewsId');
    if (idParam) {
      const id = parseInt(idParam);
      if (!isNaN(id)) {
        fetchMonthlyNewsData(id);
      }
    }
  }, [searchParams]);

  const fetchMonthlyNewsData = async (id: number) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      // Production API endpoint
      const response = await fetch(`/api/admin/monthly-news/${id}`, {
        // const response = await fetch(`http://localhost:3060/api/admin/monthly-news/${id}`, {
        method: 'GET',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to fetch monthly news');
      }

      if (data.success && data.data) {
        const monthlyNews = data.data;
        setMonthlyNewsId(monthlyNews.monthlyNewsId);
        setFormData({
          monthlyNewsTitle: monthlyNews.monthlyNewsTitle || '',
          monthlyNewsDescription: monthlyNews.monthlyNewsDescription || '',
          newsArticleFile: null
        });
        setDescCharCount(monthlyNews.monthlyNewsDescription?.length || 0);
        setExistingFileUrl(monthlyNews.monthlyNewsURL || '');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error fetching monthly news:', error);
      alert(error.message || 'Error fetching monthly news. Please try again.');
      navigate('/admin/monthly-news-directory');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'monthlyNewsDescription') {
      setDescCharCount(value.length);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      newsArticleFile: file
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.monthlyNewsTitle.trim()) {
      alert('Title is required');
      return false;
    }
    if (!formData.monthlyNewsDescription.trim()) {
      alert('Description is required');
      return false;
    }
    if (descCharCount > MAX_DESC) {
      alert(`Description exceeds the maximum character limit of ${MAX_DESC}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowConfirmation(false);
      return;
    }

    if (!monthlyNewsId) {
      alert('No monthly news ID available');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('monthlyNewsTitle', formData.monthlyNewsTitle.trim());
      submitData.append('monthlyNewsDescription', formData.monthlyNewsDescription.trim());
      if (formData.newsArticleFile) {
        submitData.append('newsArticleFile', formData.newsArticleFile);
      }
      
      // Production API endpoint
      const response = await fetch(`/api/admin/monthly-news/${monthlyNewsId}`, {
        // const response = await fetch(`http://localhost:3060/api/admin/monthly-news/${monthlyNewsId}`, {
        method: 'PUT',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to update monthly news');
      }

      alert('Monthly news updated successfully!');
      setShowConfirmation(false);
      navigate('/admin/monthly-news-directory');
    } catch (error: any) {
      console.error('Error updating monthly news:', error);
      alert(error.message || 'Error updating monthly news. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  if (isLoading) {
    return (
      <div className="job-upload-page">
        <div className="job-upload-container">
          <h1 className="job-upload-title">Loading...</h1>
        </div>
            </div>
    );
  }

  if (!monthlyNewsId) {
    return (
      <div className="job-upload-page">
        <div className="job-upload-container">
          <h1 className="job-upload-title">Edit Monthly News</h1>
          <p>No monthly news ID provided. Please navigate from the Monthly News Directory.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-upload-page">
      <div className="job-upload-container">
        <h1 className="job-upload-title">Upload Monthly News</h1>
        
        <form className="job-upload-form" onSubmit={handleFormSubmit}>
          <div className="job-upload-field">
            <label htmlFor="monthlyNewsTitle" className="job-upload-label">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="monthlyNewsTitle"
              name="monthlyNewsTitle"
              value={formData.monthlyNewsTitle}
              onChange={handleInputChange}
              className="job-upload-input"
              required
            />
          </div>

          <div className="job-upload-field">
            <label htmlFor="monthlyNewsDescription" className="job-upload-label">
              Description <span className="required">*</span>
              <span className="char-count">({descCharCount} / {MAX_DESC} characters)</span>
            </label>
            <textarea
              id="monthlyNewsDescription"
              name="monthlyNewsDescription"
              value={formData.monthlyNewsDescription}
              onChange={handleInputChange}
              className="job-upload-textarea"
              rows={8}
              maxLength={MAX_DESC}
              required
            />
            {descCharCount > MAX_DESC && (
              <div className="char-count-error">
                Character limit exceeded. Please reduce the content.
              </div>
            )}
          </div>

          <div className="job-upload-field">
            <label htmlFor="newsArticleFile" className="job-upload-label">
              News Article Upload
            </label>
            {existingFileUrl && (
              <div className="file-preview" style={{ marginBottom: '1rem' }}>
                <p>Current file: {existingFileUrl}</p>
                <a 
                  href={getFileUrl(existingFileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#16416F', textDecoration: 'underline' }}
                >
                  View current file
                </a>
              </div>
            )}
            <input
              type="file"
              id="newsArticleFile"
              name="newsArticleFile"
              onChange={handleFileChange}
              className="job-upload-file-input"
              accept=".pdf,.doc,.docx"
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
              Leave empty to keep current file
            </small>
            {formData.newsArticleFile && (
              <div className="file-preview">
                <span className="file-name">{formData.newsArticleFile.name}</span>
              </div>
            )}
          </div>

          <button type="submit" className="job-upload-submit-button">
            Submit
          </button>
        </form>
      </div>

      {/* Confirmation Overlay */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <h2 className="confirmation-title">Confirm Submission</h2>
            <p className="confirmation-message">
              Are you sure that all information filled out is correct before continuing with the submission?
            </p>
            <div className="confirmation-buttons">
              <button
                type="button"
                onClick={handleSubmit}
                className="confirmation-btn confirmation-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Submit'}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="confirmation-btn confirmation-btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditMonthlyNews;
